import { Box, Checkbox, FormControlLabel, Grid, TextField } from "@mui/material";
import * as React from "react";
import { IdTitle, IIdTitle } from "ui/Scripts/source/models/common/idTitle";
import { Translations } from "ui/Scripts/source/models/translations";
import { ConfirmationDialogResult, TaskChartValueFieldCalculationType, TaskDataRowDataType } from "ui/Scripts/source/models/enums";
import { AppUtils } from "../../../../../framework/appUtils";
import { Dialog } from "../../../../framework/Dialog/Dialog";
import { DialogButtons } from "../../../../framework/Dialog/DialogButtons";
import { GridRow } from "source/components/framework/Grid/GridRow";
import { FormField } from "ui/Scripts/source/components/framework/FormField";
import { DialogItemsNav } from "source/components/framework/Dialog/DialogItemsNav";
import { useEffect, useRef, useState } from "react";
import { IdTitleDropdown } from "ui/Scripts/source/components/framework/Dropdown/IdTitleDropdown";
import { TaskTypeChartValueField } from "ui/Scripts/source/models/task/taskTypeChartValueField";
import { TaskTypeCol } from "ui/Scripts/source/models/task/taskTypeCol";
import { ITaskEditTypeItem } from "ui/Scripts/source/models/task/taskEditTypeItem";

export type ValueFieldDialogItem = TaskTypeChartValueField & {
}

const ValueFieldCalculationTypeOptions: IIdTitle[] = [
    IdTitle.createIdTitle(TaskChartValueFieldCalculationType.Sum.toString(10), Translations.TaskChartValueFieldCalculationTypeSum),
    IdTitle.createIdTitle(TaskChartValueFieldCalculationType.Min.toString(10), Translations.TaskChartValueFieldCalculationTypeMin),
    IdTitle.createIdTitle(TaskChartValueFieldCalculationType.Max.toString(10), Translations.TaskChartValueFieldCalculationTypeMax),
    IdTitle.createIdTitle(TaskChartValueFieldCalculationType.Average.toString(10), Translations.TaskChartValueFieldCalculationTypeAverage),
    IdTitle.createIdTitle(TaskChartValueFieldCalculationType.Median.toString(10), Translations.TaskChartValueFieldCalculationTypeMedian),
    IdTitle.createIdTitle(TaskChartValueFieldCalculationType.Count.toString(10), Translations.TaskChartValueFieldCalculationTypeCount),
];

export const taskChartValueFieldCalculationTypeToString = (calculationType: TaskChartValueFieldCalculationType): string => {
    return ValueFieldCalculationTypeOptions.find(option => option.id === calculationType.toString(10))?.title || "";
};

interface IChartDesignerValueFieldDialogProp {
    taskType: ITaskEditTypeItem;
    availableFields: IIdTitle[];
    item: ValueFieldDialogItem;
    isNew: boolean;
    hasPrevious: boolean;
    hasNext: boolean;
    onPrevious: () => void;
    onNext: () => void;
    onSaved: (item: ValueFieldDialogItem) => void;
    onClose: () => void;
    onRemove: (title: string) => void;
}

export const getValueFieldCalculationTypeOptions = (taskType: ITaskEditTypeItem, field: string): IIdTitle[] => {
    const col = taskType.formCols.find(c => c.field === field);
    if (!col || !TaskTypeCol.isNumberValueField(col) || col.dataType === TaskDataRowDataType.Date) {
        return ValueFieldCalculationTypeOptions.filter(option => option.id === TaskChartValueFieldCalculationType.Count.toString(10));
    }
    return ValueFieldCalculationTypeOptions;
};

export const ChartDesignerValueFieldDialog = (props: IChartDesignerValueFieldDialogProp) => {
    const orgStateHash = useRef("");
    
    const [state, setState] = useState<ValueFieldDialogItem>({
        title: props.item.title,
        calculationType: props.item.calculationType,
        field: props.item.field,
        showDataLabels: props.item.showDataLabels,
    });

    const [calculationTypeOptions, setCalculationTypeOptions] = useState<IIdTitle[]>(getValueFieldCalculationTypeOptions(props.taskType, props.item.field));
    const [isNew, setIsNew] = useState(props.isNew);

    const handleChangeField = (field: string) => {
        const newCalculationTypeOptions = getValueFieldCalculationTypeOptions(props.taskType, field);
        const calculationType = parseInt(newCalculationTypeOptions[0].id, 10);
        const defaultTitle = props.availableFields.find(f => f.id === field)?.title ?? props.item.field;
        setState((old) => ({
            ...old,
            field,
            calculationType,
            title: calculationType === TaskChartValueFieldCalculationType.Count
                ? Translations.TaskChartValueFieldCalculationTypeCount
                : defaultTitle
        }));
        setCalculationTypeOptions(newCalculationTypeOptions);
    };

    const handleChangeCalculationType = (id: string) => {
        const calculationType = parseInt(id || calculationTypeOptions[0].id, 10);
        setState((old) => ({
            ...old,
            calculationType,
            title: calculationType === TaskChartValueFieldCalculationType.Count && old.title === (props.availableFields.find(f => f.id === old.field)?.title || "")
                ? Translations.TaskChartValueFieldCalculationTypeCount
                : old.title
        }));
    };

    const saveToDb = async (): Promise<boolean> => {
        orgStateHash.current = getHashFromState(state);
        props.onSaved({ ...state });
        setIsNew(false);
        return true;
    };

    const getHashFromState = (state: ValueFieldDialogItem): string => {
        return JSON.stringify(state);
    };

    useEffect(() => {
        orgStateHash.current = getHashFromState(state);
    }, []);

    useEffect(() => {
        const checkPendingSave = async () => {
            const cancelResult = await AppUtils.cancel(getHashFromState(state), orgStateHash.current, null);
            if (cancelResult === ConfirmationDialogResult.Cancel) return;
            if (cancelResult === ConfirmationDialogResult.Yes) {
                await saveToDb();
            }
            const defaultTitle = props.taskType.formCols.find(col => col.field === props.item.field)?.title ?? props.item.field;
            const newState = {
                title: props.item.title || defaultTitle,
                calculationType: props.item.calculationType,
                field: props.item.field,
                showDataLabels: props.item.showDataLabels
            };
            setState(newState);
            setCalculationTypeOptions(getValueFieldCalculationTypeOptions(props.taskType, newState.field));
            orgStateHash.current = getHashFromState(newState);
        };

        checkPendingSave();
    }, [props.item]);
 
    const handleSave = async (closeDialog: boolean) => {
        await saveToDb();
        if (!closeDialog) {
            return;
        }
        props.onClose();
    };

    const handleRemove = async () => {
        if (isNew) return;
        props.onRemove(props.item.field);
    };

    const handleCancel = async () => {
        const cancelResult = await AppUtils.cancel(getHashFromState(state), orgStateHash.current, null);
        if (cancelResult === ConfirmationDialogResult.Cancel) return;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            await handleSave(true);
        } else {
            props.onClose();
        }
    };

    const canMoveOut = async (): Promise<boolean> => {
        return await AppUtils.canMoveOut(getHashFromState(state), orgStateHash.current,
            saveToDb,
            null,
            (newStateHash) => { orgStateHash.current = newStateHash; }
        );
    };

    const handlePrevious = async () => {
        if (!await canMoveOut()) return;
        props.onPrevious();
    };

    const handleNext = async () => {
        if (!await canMoveOut()) return;
        props.onNext();
    };

    if (!props.item) return null;

    return (
        <Dialog
            id="chartDesignerValueFieldDialog"
            maxWidth="sm"
            title={isNew
                ? Translations.AddChartValueField
                : (Translations.ChartValueField + ": " + props.item.title)}
            body={
                <Box display="flex" flexDirection="column">
                    <GridRow>
                        <Grid item sm={12}>
                            <FormField title={Translations.ChartValueField} fullWidth margin="0">
                                <IdTitleDropdown
                                    selectedId={state.field}
                                    items={props.availableFields}
                                    onChange={handleChangeField}
                                />
                            </FormField>
                        </Grid>
                    </GridRow>
                    <GridRow>
                        <Grid item sm={12}>
                            <FormField title={Translations.ChartValueCalculation} fullWidth>
                                <IdTitleDropdown
                                    selectedId={state.calculationType.toString(10)}
                                    items={calculationTypeOptions}
                                    onChange={handleChangeCalculationType}
                                />
                            </FormField>
                        </Grid>
                    </GridRow>
                    <GridRow>
                        <Grid item sm={12}>
                            <FormField title={Translations.Title} fullWidth>
                                <TextField
                                    value={state.title}
                                    onChange={(e) => setState(old => ({ ...old, title: e.target.value }))}
                                />
                            </FormField>
                        </Grid>
                    </GridRow>
                    <GridRow>
                        <Grid item sm={12}>
                            <FormField title="" fullWidth>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            title={Translations.TaskChartShowDataLabels}
                                            sx={{ paddingBottom: 0, paddingTop: 0 }}
                                            checked={!!state.showDataLabels}
                                            onChange={(_e, checked) => setState(old => ({ ...old, showDataLabels: checked })) }
                                        />
                                    }
                                    label={Translations.TaskChartShowDataLabels}
                                    className="checkbox"
                                />
                            </FormField>
                        </Grid>
                    </GridRow>
                </Box>
            }
            actions={
                <Box display="flex" justifyContent="space-between" width="100%">
                    <DialogItemsNav
                        hasPrevious={!isNew && props.hasPrevious}
                        hasNext={!isNew && props.hasNext}
                        onPrevious={handlePrevious}
                        onNext={handleNext}
                    />
                    <DialogButtons
                        isNew={isNew}
                        saveTitle={Translations.OK}
                        onCancel={handleCancel}
                        onRemove={!isNew ? handleRemove : null}
                        onSave={() => handleSave(false)}
                    />
                </Box>
            }
            onClose={handleCancel}
        />
    );
};